'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import logNullStateCTAClicked from '@alltrails/amplitude/events/logNullStateCTAClicked';
import logNullStateViewed from '@alltrails/amplitude/events/logNullStateViewed';
import NullState from '@alltrails/denali/components/NullState';
import Tent from '@alltrails/denali/illustrations/Tent';
import styles from './ConditionsErrorCard.module.scss';

type ConditionsErrorCardProps = {
  className?: string;
  customRouteId?: number;
  onRetry: () => void;
  showIllustration?: boolean;
  trailId?: number;
};

const ConditionsErrorCard = ({ className, customRouteId, onRetry, showIllustration, trailId }: ConditionsErrorCardProps) => {
  const intl = useIntl();
  const isCustomRoute = customRouteId !== undefined;

  useEffect(() => {
    if (isCustomRoute) {
      logNullStateViewed({ null_location: 'custom route conditions card', null_state: 'server error', custom_route_id: String(customRouteId) });
    } else {
      logNullStateViewed({ null_location: 'trail conditions card', null_state: 'server error', trail_id: trailId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only log on first render
  }, []);

  const hasLoggedRetryEvent = useRef(false);
  const onClick = useCallback(() => {
    if (!hasLoggedRetryEvent.current) {
      hasLoggedRetryEvent.current = true;
      if (isCustomRoute) {
        logNullStateCTAClicked({
          null_action: 'retry',
          null_location: 'custom route conditions card',
          null_state: 'server error',
          custom_route_id: String(customRouteId)
        });
      } else {
        logNullStateCTAClicked({
          null_action: 'retry',
          null_location: 'trail conditions card',
          null_state: 'server error',
          trail_id: trailId
        });
      }
    }
    onRetry();
  }, [customRouteId, isCustomRoute, onRetry, trailId]);

  return (
    <div className={classNames(styles.container, className)}>
      <NullState
        buttons={{ default: { onClick, text: intl.formatMessage({ defaultMessage: 'Retry' }) } }}
        className={styles.nullState}
        description={intl.formatMessage({ defaultMessage: 'We’re having trouble loading conditions for this trail' })}
        illustration={showIllustration ? { Component: Tent } : undefined}
        noTopMargin
        title={intl.formatMessage({ defaultMessage: 'Let’s try that again' })}
      />
    </div>
  );
};

export default ConditionsErrorCard;
